import React, { useId } from "react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import CustomTextArea from "../CustomTextArea/CustomTextArea";
import "../CustomTheme/customTheme.css";
import "./customInputField.css";

/**
 * CustomInputField — single-line input (text, number, email, password, ...).
 * For multi-line text use CustomTextArea.
 *
 * Props: label | title (old name), name, id, isRequired, hint, error,
 *        colClass, type, placeholder, onChange(value) | setInputText(value),
 *        className / style (applied to the <input>), plus any native input prop.
 */
const CustomInputField = ({
  label,
  title, // kept for backward compatibility with the old prop name
  name,
  id,
  isRequired,
  hint,
  error,
  colClass,
  noMargin = false,
  className,
  style,
  type = "text",
  placeholder,
  onChange,
  setInputText,
  ...rest
}) => {
  const autoId = useId();
  const inputId = id || name || `cif-${autoId}`;
  const ids = getFieldIds(inputId);

  // `type="textarea"` is not a real <input> type — hand it to CustomTextArea
  if (type === "textarea") {
    return (
      <CustomTextArea
        label={label ?? title}
        name={name}
        id={inputId}
        isRequired={isRequired}
        hint={hint}
        error={error}
        colClass={colClass}
        noMargin={noMargin}
        placeholder={placeholder}
        onChange={onChange}
        setInputText={setInputText}
        className={className}
        style={style}
        {...rest}
      />
    );
  }

  const handleChange = (e) => {
    onChange?.(e.target.value);
    setInputText?.(e.target.value);
  };

  return (
    <CustomFormField
      label={label ?? title}
      isRequired={isRequired}
      htmlFor={inputId}
      hint={hint}
      error={error}
      colClass={colClass}
      noMargin={noMargin}
    >
      <input
        id={inputId}
        name={name}
        type={type}
        placeholder={placeholder}
        onChange={handleChange}
        aria-invalid={Boolean(error)}
        aria-required={Boolean(isRequired)}
        aria-describedby={ids.describedBy(error, hint)}
        className={`cui-control cif-input${error ? " cui-control-error" : ""}${
          className ? ` ${className}` : ""
        }`}
        style={style}
        {...rest}
      />
    </CustomFormField>
  );
};

export default CustomInputField;
